import { stateNameAbbrMap } from '@mortgage-pos/data/states'

/**
 * Address normalization utilities for matching addresses from different sources
 */

interface NormalizedAddress {
  street: string
  city: string
  state: string
  zipCode: string
}

const DIRECTIONAL_MAP: Record<string, string> = {
  EAST: 'E',
  WEST: 'W',
  NORTH: 'N',
  SOUTH: 'S',
  NORTHEAST: 'NE',
  NORTHWEST: 'NW',
  SOUTHEAST: 'SE',
  SOUTHWEST: 'SW',
}

const STREET_TYPE_MAP: Record<string, string> = {
  STREET: 'ST',
  AVENUE: 'AVE',
  BOULEVARD: 'BLVD',
  DRIVE: 'DR',
  ROAD: 'RD',
  LANE: 'LN',
  COURT: 'CT',
  PLACE: 'PL',
  CIRCLE: 'CIR',
  PARKWAY: 'PKWY',
  TERRACE: 'TER',
  WAY: 'WAY',
}

/**
 * Normalizes a street address for comparison by:
 * - Converting to uppercase
 * - Removing all punctuation and special characters
 * - Collapsing multiple spaces to single space
 * - Standardizing directional abbreviations (E, W, N, S, NE, etc.)
 * - Standardizing street type abbreviations (St, Ave, Blvd, etc.)
 */
export function normalizeStreetAddress(
  address: string | null | undefined
): string {
  if (!address || typeof address !== 'string') {
    return ''
  }

  // Uppercase and trim
  let normalized = address.toUpperCase().trim()

  // Remove punctuation
  normalized = normalized.replace(/[.,-]/g, ' ')

  // Collapse multiple spaces
  normalized = normalized.replace(/\s+/g, ' ').trim()

  // Process words to handle stuck directionals and do replacements
  const words = normalized.split(' ')
  const processedWords: string[] = []

  for (let i = 0; i < words.length; i++) {
    const word = words[i]

    // Check if word is a stuck directional like "2240E"
    if (/^\d+[NSEW]$/.test(word)) {
      const num = word.slice(0, -1)
      const dir = word.slice(-1)
      processedWords.push(num, dir)
      continue
    }

    if (DIRECTIONAL_MAP[word]) {
      processedWords.push(DIRECTIONAL_MAP[word])
      continue
    }

    if (STREET_TYPE_MAP[word]) {
      processedWords.push(STREET_TYPE_MAP[word])
      continue
    }

    processedWords.push(word)
  }

  return processedWords.join(' ').trim()
}

/**
 * Normalizes a city name for comparison
 */
export function normalizeCity(city: string | null | undefined): string {
  if (!city || typeof city !== 'string') {
    return ''
  }

  return city.toUpperCase().trim().replace(/\s+/g, ' ')
}

/**
 * Normalizes a state abbreviation
 */
export function normalizeState(state: string | null | undefined): string {
  if (!state || typeof state !== 'string') {
    return ''
  }

  const normalized = state.toUpperCase().trim()
  return stateNameAbbrMap[normalized] ?? normalized
}

/**
 * Normalizes a zip code
 * Returns only the first 5 digits for comparison
 */
export function normalizeZipCode(zipCode: string | null | undefined): string {
  if (!zipCode || typeof zipCode !== 'string') {
    return ''
  }

  const digits = zipCode.replace(/\D/g, '')

  return digits.slice(0, 5)
}

/**
 * Normalizes all address components for address matching
 */
export function normalizeAddress(address: {
  street?: string | null
  city?: string | null
  state?: string | null
  zipCode?: string | null
}): NormalizedAddress {
  return {
    street: normalizeStreetAddress(address.street),
    city: normalizeCity(address.city),
    state: normalizeState(address.state),
    zipCode: normalizeZipCode(address.zipCode),
  }
}

/**
 * Compares two addresses for address matching
 * Returns true if addresses match after normalization
 */
export function addressesMatch(
  address1: {
    street?: string | null
    city?: string | null
    state?: string | null
    zipCode?: string | null
  },
  address2: {
    street?: string | null
    city?: string | null
    state?: string | null
    zipCode?: string | null
  }
): boolean {
  const normalized1 = normalizeAddress(address1)
  const normalized2 = normalizeAddress(address2)

  return (
    normalized1.street === normalized2.street &&
    normalized1.city === normalized2.city &&
    normalized1.state === normalized2.state &&
    normalized1.zipCode === normalized2.zipCode
  )
}
